import * as Sentry from "@sentry/react";

export const APP_OPEN_BUDGET_MS = 8000;

type AppOpenOutcome =
  | "ready"
  | "login"
  | "terms"
  | "legacy"
  | "no_plan"
  | "unavailable"
  | "retry";

type StageOutcome = "ok" | "timeout" | "error" | "cancelled" | "skipped";

type SpanHandle = {
  end: (outcome: StageOutcome) => void;
};

const monotonicNow = () =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

let attemptStartedAt = 0;
let deadlineAt = 0;
let attemptStarted = false;
let completed = false;
let rootSpan: ReturnType<typeof Sentry.startInactiveSpan> | null = null;
let traceTimer: ReturnType<typeof setTimeout> | null = null;

function endRootAsTimeout(): void {
  if (!rootSpan) return;
  rootSpan.setAttribute("app.open.outcome", "deadline");
  rootSpan.setAttribute("app.vitals.ttid.value", monotonicNow() - attemptStartedAt);
  rootSpan.setStatus({ code: 2, message: "deadline" });
  rootSpan.end();
  rootSpan = null;
  traceTimer = null;
}

export function startAppOpenTelemetry(reason = "page_load"): void {
  if (!attemptStarted) {
    attemptStartedAt = monotonicNow();
    deadlineAt = attemptStartedAt + APP_OPEN_BUDGET_MS;
    attemptStarted = true;
    completed = false;
  }
  if (rootSpan || !Sentry.getClient()) return;

  rootSpan = Sentry.startInactiveSpan({
    name: "app.open",
    op: "ui.load",
    forceTransaction: true,
    attributes: {
      "app.vitals.start.type": "cold",
      "app.vitals.start.reason": reason,
    },
  });

  traceTimer = setTimeout(endRootAsTimeout, Math.max(1, deadlineAt - monotonicNow()));
}

export function restartAppOpenAttempt(): void {
  if (traceTimer) clearTimeout(traceTimer);
  traceTimer = null;
  if (rootSpan) {
    rootSpan.setAttribute("app.open.outcome", "retry");
    rootSpan.setStatus({ code: 2, message: "retry" });
    rootSpan.end();
    rootSpan = null;
  }

  attemptStartedAt = monotonicNow();
  deadlineAt = attemptStartedAt + APP_OPEN_BUDGET_MS;
  attemptStarted = true;
  completed = false;
  startAppOpenTelemetry("retry");
}

/**
 * Orçamento restante do primeiro boot. Depois que uma tela útil foi entregue,
 * operações de navegação/retry recebem seu teto normal, sem herdar um deadline
 * antigo da abertura do WebView.
 */
export function getAppOpenRemainingMs(maximumMs = APP_OPEN_BUDGET_MS): number {
  if (!attemptStarted || completed) return maximumMs;
  return Math.max(1, Math.min(maximumMs, deadlineAt - monotonicNow()));
}

export function startAppOpenStage(name: string): SpanHandle {
  if (!rootSpan || !Sentry.getClient()) return { end: () => {} };

  const span = Sentry.startInactiveSpan({
    name,
    op: "ui.load",
    parentSpan: rootSpan,
  });
  let ended = false;

  return {
    end(outcome) {
      if (ended) return;
      ended = true;
      span.setAttribute("app.open.stage_outcome", outcome);
      span.setStatus({ code: outcome === "ok" || outcome === "skipped" ? 1 : 2 });
      span.end();
    },
  };
}

export function completeAppOpen(screen: string, outcome: AppOpenOutcome): void {
  if (completed) return;
  completed = true;
  if (traceTimer) clearTimeout(traceTimer);
  traceTimer = null;

  if (!rootSpan) return;
  rootSpan.setAttributes({
    "app.vitals.start.screen": screen,
    "app.vitals.ttid.value": monotonicNow() - attemptStartedAt,
    "app.open.outcome": outcome,
  });
  rootSpan.setStatus({ code: outcome === "unavailable" ? 2 : 1 });
  rootSpan.end();
  rootSpan = null;
}

/** Somente para isolar testes do estado de módulo. */
export function __resetAppOpenForTests(): void {
  if (traceTimer) clearTimeout(traceTimer);
  traceTimer = null;
  rootSpan?.end();
  rootSpan = null;
  attemptStartedAt = 0;
  deadlineAt = 0;
  attemptStarted = false;
  completed = false;
}

